import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  WritableSignal,
  signal,
} from '@angular/core';
import { ControlsOf, FormControl, FormGroup } from '@ngneat/reactive-forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AccountService, BankAccountClient } from '@vinnova/oreg-lib';
import { ModalRef, ModalService, ToastV2Service } from '@vinnova/ui';
import { Observable, Subscription, of, switchMap, take } from 'rxjs';
import { FormQuestionStandard } from '../../../core/models/form-group-mapping.model';
import { ReadonlyState } from '../../../core/models/form.model';
import {
  Account,
  VinnovaStatus,
} from '../../../core/models/question/question-account.model';
import { QuestionStructureWorksite } from '../../../core/models/question/question-worksite.model';
import { Question } from '../../../core/models/question/question.model';
import { RendererFormsQuery } from '../../../core/queries/renderer-forms.query';
import { BaseWorksiteService } from '../../../core/services/base-worksite.service';
import { FormValidationService } from '../../../core/services/form-validation.service';
import { QuestionNumberService } from '../../../core/services/question-number.service';
import { QuestionValueChangeService } from '../../../core/services/question-value-change.service';

@UntilDestroy()
@Component({
  selector: 'lib-question-account',
  templateUrl: './question-account.component.html',
  styleUrls: ['./question-account.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class QuestionAccountComponent implements OnInit, OnChanges {
  @ViewChild('accountModal', { static: false }) set content(content) {
    // initially setter gets called with undefined
    if (content) {
      this.accountModal = content;
    }
  }

  private _question: Question;
  @Input() set question(value: Question) {
    this._question = value;
  }
  get question() {
    return this._question;
  }

  @Input() labelPrefix: string;
  @Input() repeaterGroupId: string;

  _questionForm: FormGroup<ControlsOf<FormQuestionStandard>>;
  @Input()
  set questionForm(form: FormGroup<ControlsOf<FormQuestionStandard>>) {
    this._questionForm = form;
    this.value?.touch$.pipe(take(1)).subscribe(() => {
      this.cdr.markForCheck();
    });
  }

  get questionForm() {
    return this._questionForm;
  }

  get structure() {
    return this.question.structure as QuestionStructureWorksite;
  }

  get value(): FormControl<string> {
    return this.questionForm?.controls.value;
  }

  worksiteId: string;
  accountModal: TemplateRef<unknown>;
  accountModalRef: ModalRef;
  readOnly$: Observable<ReadonlyState>;
  activeLang$: Observable<string>;
  questionNumber$: Observable<unknown>;
  errorMessage$: Observable<string>;
  valueChangeSubscription: Subscription;
  patchValueSubscription: Subscription;
  accountAnswer: WritableSignal<Account> = signal(undefined);
  VinnovaStatus = VinnovaStatus;

  constructor(
    private cdr: ChangeDetectorRef,
    private modalService: ModalService,
    private translocoService: TranslocoService,
    private questionValueChangeService: QuestionValueChangeService,
    private questionNumberService: QuestionNumberService,
    private formValidationService: FormValidationService,
    private formsQuery: RendererFormsQuery,
    private accountClient: BankAccountClient,
    private toastService: ToastV2Service,
    private baseWorksiteService: BaseWorksiteService,
    private accountService: AccountService,
  ) {}

  ngOnInit(): void {
    this.readOnly$ = this.formsQuery.selectReadOnly();
    this.activeLang$ = this.translocoService.langChanges$;
    this.worksiteId = this.baseWorksiteService.getCoordinatorWorksiteId();
    // populate initial answers from the db
    this.questionValueChangeService.patchInitialValue(
      this.question.id,
      this.repeaterGroupId,
      this.value,
      this.question,
    );

    if (this.value.value) {
      this.accountClient
        .getAccount(this.value.value)
        .pipe(untilDestroyed(this))
        .subscribe((account) => {
          this.accountAnswer.set(account);
          if (account.vinnovaStatus.toString() === 'New') {
            this.accountService.setNewAccount(account.id);
          }
        });
    }

    this.questionNumber$ = this.questionNumberService.getQuestionNumberForLabel(
      this.question.id,
    );

    this.errorMessage$ = this.readOnly$.pipe(
      switchMap((readonly) => {
        switch (readonly) {
          case ReadonlyState.Editable: {
            return this.formValidationService
              .questionInvalid(this.value, this.question.id)
              .pipe(
                switchMap((invalid) => {
                  return invalid
                    ? this.translocoService.selectTranslate(
                        'oreg_account_add_account',
                      )
                    : of(undefined);
                }),
              );
          }
          case ReadonlyState.Locked: {
            return this.value.invalid
              ? this.translocoService.selectTranslate(
                  'oreg_account_add_account',
                )
              : of(undefined);
          }
          case ReadonlyState.Reader: {
            return of(undefined);
          }
        }
      }),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['repeaterGroupId']) {
      if (this.valueChangeSubscription) {
        this.valueChangeSubscription.unsubscribe();
      }
      if (this.patchValueSubscription) {
        this.valueChangeSubscription.unsubscribe();
      }

      this.valueChangeSubscription = this.questionValueChangeService
        .valueChangeListener(
          this.value,
          {
            questionId: this.question.id,
            repeaterGroupId: this.repeaterGroupId,
          },
          this.question,
        )
        .pipe(untilDestroyed(this))
        .subscribe();

      this.patchValueSubscription = this.questionValueChangeService
        .patchValueListener(this.question, this.repeaterGroupId, this.value)
        .pipe(untilDestroyed(this))
        .subscribe(() => this.setReadonly());
    }
  }

  setReadonly() {
    if (this.formsQuery.getReadOnly()) {
      if (this.value.value) {
        this.accountClient
          .getAccount(this.value.value)
          .pipe(take(1))
          .subscribe((account) => {
            this.accountAnswer.set(account);
          });
      } else {
        this.accountAnswer.set(undefined);
      }
    }
  }

  removeAccount() {
    if (this.formsQuery.getIsLoading()) return;
    if (this.accountAnswer().vinnovaStatus.toString() === 'New') {
      this.accountClient
        .deleteAccount(this.accountAnswer().id)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: () => {
            this.accountService.removeNewAccount();
          },
          error: (err) => {
            this.toastService.error(err);
          },
        });
    }
    this.accountAnswer.set(undefined);
    this.value.patchValue(undefined);
  }

  onOpenOrEditAccountModal() {
    if (this.formsQuery.getIsLoading()) return;
    this.accountModalRef = this.modalService.show(this.accountModal, {
      backdrop: 'static',
      class: 'modal-m modal-fullscreen-md-down',
    });
  }

  onCloseAccountModal() {
    this.value.markAsDirty();
    this.value.markAsTouched();
    this.accountModalRef.hide();
  }

  onAccountSubmitted(account: Account) {
    if (account) {
      this.value.markAsDirty();
      this.value.markAsTouched();
      this.value.patchValue(account.id);
      this.accountAnswer.set(account);
    }
    this.accountModalRef.hide();
  }
}
